import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { apiCall } from '../utils/api';

function Projects({ user, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await apiCall('/api/projects');
      const data = await response.json();
      setProjects(data.projects);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);

      const response = await apiCall('/api/projects', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Project created!');
        setShowModal(false);
        setName('');
        setDescription('');
        fetchProjects();
      } else {
        toast.error('Failed to create project');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its QR codes?')) return;

    try {
      const response = await apiCall(`/api/projects/${projectId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Project deleted');
        fetchProjects();
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalQRCodes = projects.reduce((sum, p) => sum + (p.qr_count || 0), 0);
  const totalScans = projects.reduce((sum, p) => sum + (p.total_scans || 0), 0);

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} user={user} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle="Dashboard" />
        
        <div className="page-content">
          {/* Stats Row */}
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="stat-card">
                <div className="stat-card-icon">📊</div>
                <h3>{projects.length}</h3>
                <p>Total Projects</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card success">
                <div className="stat-card-icon">📱</div>
                <h3>{totalQRCodes}</h3>
                <p>QR Codes</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card info">
                <div className="stat-card-icon">👁️</div>
                <h3>{totalScans}</h3>
                <p>Total Scans</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="stat-card warning">
                <div className="stat-card-icon">🔥</div>
                <h3>{totalQRCodes > 0 ? Math.round(totalScans / totalQRCodes) : 0}</h3>
                <p>Avg Scans/QR</p>
              </div>
            </div>
          </div>

          {/* Page Header */}
          <div className="page-header d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4>My Projects</h4>
              <p className="text-muted">Organize and manage your QR code campaigns</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => setShowModal(true)}
              data-testid="create-project-button"
            >
              <span style={{ fontSize: '1.2rem' }}>+</span> New Project
            </button>
          </div>

          {/* Projects Grid */}
          {projects.length === 0 ? (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">📁</div>
                <h3>No projects yet</h3>
                <p className="text-muted">Create your first project to organize your QR codes</p>
                <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                  Create Project
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="card project-card"
                  onClick={() => navigate(`/projects/${project.id}`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="card-header">
                    <h5 className="card-title mb-0">{project.name}</h5>
                  </div>
                  
                  <p className="text-muted mb-3" style={{ fontSize: '0.875rem', minHeight: '40px' }}>
                    {project.description || 'No description provided'}
                  </p>

                  <div className="row mb-3">
                    <div className="col-6">
                      <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--gray-100)', borderRadius: 'var(--border-radius)' }}>
                        <h4 style={{ color: 'var(--primary)', marginBottom: '0.25rem' }}>{project.qr_count}</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>QR Codes</p>
                      </div>
                    </div>
                    <div className="col-6">
                      <div style={{ textAlign: 'center', padding: '1rem', background: 'var(--gray-100)', borderRadius: 'var(--border-radius)' }}>
                        <h4 style={{ color: 'var(--success)', marginBottom: '0.25rem' }}>{project.total_scans}</h4>
                        <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Scans</p>
                      </div>
                    </div>
                  </div>

                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '1rem' }}>
                    Created {new Date(project.created_at).toLocaleDateString()}
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-light"
                      style={{ flex: 1 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.id}`);
                      }}
                    >
                      View Details
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteProject(project.id);
                      }}
                      data-testid={`delete-project-${project.id}`}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Create New Project</h5>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--gray-600)' }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={createProject} id="create-project-form">
                <div className="mb-3">
                  <label className="label">Project Name *</label>
                  <input
                    type="text"
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="e.g., Summer Campaign 2024"
                    data-testid="project-name-input"
                  />
                </div>
                <div className="mb-3">
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows="3"
                    placeholder="Brief description of this project..."
                    data-testid="project-description-input"
                  />
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setShowModal(false)}
                data-testid="cancel-create-project"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="create-project-form"
                className="btn btn-primary"
                disabled={creating}
                data-testid="submit-create-project"
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
